import React, { useState } from "react";
import html2canvas from "html2canvas";
import PptxGenJS from "pptxgenjs";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { tooltipClasses } from "@mui/material/Tooltip";
import GetAppIcon from "@material-ui/icons/GetApp"; // Import the icon from the appropriate library

// Define CustomWidthTooltip
const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "8rem",
    fontSize: "1rem",
    color: "black",
    textAlign: "center",
    background: "white",
    border: "0.5px solid lightgrey",
  },
});

function ExportPPtx({ chartRef, typographyRef, title, description, fileName }) {
  const [tooltipText, setTooltipText] = useState(
    "Download as PowerPoint slide"
  ); // Tooltip text state
  const [loading, setLoading] = useState(false); // Loading state

  const iconStyles = {
    width: "20px",
    height: "20px",
    marginRight: "3px",
    color: "transparent",
    fill: "#A9A9A9",
  };

  const handleMouseEnter = () => {
    if (tooltipText !== "PowerPoint Slide Downloaded") {
      setTooltipText("Download as PowerPoint slide"); // Show initial tooltip on hover
    }
  };

  const handleMouseLeave = () => {
    if (tooltipText !== "PowerPoint Slide Downloaded") {
      setTooltipText("Download as PowerPoint slide"); // Reset tooltip on mouse leave
    }
  };

  const handleExport = async () => {
    setLoading(true); // Show the loading spinner

    // Capture the chart as an image with higher resolution and a white background
    const chartImage = await html2canvas(chartRef.current, {
      backgroundColor: "#FFFFFF", // Force white background to avoid dark shades
      scale: 2, // Increase scale for higher resolution
    });

    // Create the PowerPoint presentation
    const pptx = new PptxGenJS();

    // Create a slide with a white background
    const chartSlide = pptx.addSlide({ background: { fill: "FFFFFF" } });

    const addImageWithTitleToSlide = (
      slide,
      imageBase64,
      imageWidth,
      imageHeight,
      title
    ) => {
      const slideWidthInches = 7.5;
      const slideHeightInches = 4.625;

      const imageAspectRatio = imageWidth / imageHeight;
      const slideAspectRatio = slideWidthInches / slideHeightInches;

      let widthInches;
      let heightInches;

      if (imageAspectRatio > slideAspectRatio) {
        widthInches = slideWidthInches;
        heightInches = widthInches / imageAspectRatio;
      } else {
        heightInches = slideHeightInches;
        widthInches = heightInches * imageAspectRatio;
      }

      const xPos = (slideWidthInches - widthInches) / 2;
      const yPos = (slideHeightInches - heightInches) / 2;

      // Add title to the slide
      slide.addText(title, {
        x: xPos + 1.2,
        y: yPos,
        w: widthInches,
        h: 0.5,
        fontSize: 12,
        align: "center",
        textWrap: false,
      });

      // Add description below the title, aligned left if available
      if (description) {
        slide.addText(description, {
          x: 1.2, // Align to the left side, same as title
          y: 1.69, // Position below the title
          w: widthInches,
          h: 0.2,
          fontSize: 7,
          align: "left",
          bold: true, // Set text to bold
        });
      }

      // Add image to the slide
      slide.addImage({
        data: imageBase64,
        x: xPos + 1.2,
        y: yPos + 0.5,
        w: widthInches,
        h: heightInches,
      });
    };

    // Add the image and title to the slide
    addImageWithTitleToSlide(
      chartSlide,
      chartImage.toDataURL(),
      chartImage.width,
      chartImage.height,
      title
    );

    // Save the PowerPoint presentation
    await pptx.writeFile(`SkyGeni-${fileName ? fileName : title}.pptx`);

    // Hide the loading spinner after the export is complete
    setLoading(false);

    // Change the tooltip text to "PowerPoint Slide Downloaded" after exporting
    setTooltipText("PowerPoint Slide Downloaded");

    // Set a timer to reset the tooltip text back to "Export PPTX" after 5 seconds
    setTimeout(() => {
      setTooltipText("Download as PowerPoint slide");
    }, 5000);
  };

  return (
    <div
      style={{ display: "flex", alignItems: "center", position: "relative" }}
    >
      <CustomWidthTooltip title={tooltipText} placement="left">
        <button
          onClick={handleExport}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            position: "relative", // Ensures the button is a containing element for the loading spinner
          }}
        >
          <GetAppIcon style={{ color: "#808080", ...iconStyles }} />

          {loading && (
            <div
              style={{
                position: "absolute",
                top: "0",
                left: "0",
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 2, // Ensure it is above the icon
              }}
            >
              <img
                src="/test_loading.gif"
                alt="Loading..."
                style={{
                  width: "20px",
                  height: "20px",
                }}
              />
            </div>
          )}
        </button>
      </CustomWidthTooltip>
    </div>
  );
}

export default ExportPPtx;
